import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { adminApi } from "@/lib/apiClient";
import PrivacyDisclaimer from "@/components/PrivacyDisclaimer";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend sets the admin_token httpOnly cookie; no token kept in localStorage.
      await adminApi.post("/admin/login", { email, password });
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      console.error("admin login failed", err);
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen page-tinted flex items-center justify-center px-6 relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#F4B860] opacity-[0.2] blur-[120px]"/>
      <div aria-hidden className="pointer-events-none absolute bottom-[-180px] -left-32 h-[420px] w-[420px] rounded-full bg-[#A7C7E7] opacity-[0.22] blur-[110px]"/>

      <div className="w-full max-w-md relative">
        <Link to="/" className="block mx-auto mb-8 w-fit">
          <img src="/wah-logo.png" alt="Wealth Accelerator Hub" className="h-14 w-auto"/>
        </Link>
        <div className="rounded-2xl border border-[#E5E2DA] bg-white/85 backdrop-blur-xl p-8 md:p-10 shadow-[0_4px_30px_rgba(28,29,28,0.06)]">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mb-3">
            <Lock className="h-3.5 w-3.5"/> Advisor portal
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium">Sign in</h1>
          <p className="text-[#5B5E5B] mt-2">Private access for the practice administrator.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Email</Label>
              <Input data-testid="admin-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="advisor@wealthacceleratorhub.com" required
                className="h-12 rounded-xl border-[#E5E2DA] bg-white focus-visible:ring-[#1A3626]/20 focus-visible:ring-2 focus-visible:border-[#1A3626]"/>
            </div>
            <div>
              <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Password</Label>
              <Input data-testid="admin-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required
                className="h-12 rounded-xl border-[#E5E2DA] bg-white focus-visible:ring-[#1A3626]/20 focus-visible:ring-2 focus-visible:border-[#1A3626]"/>
            </div>
            <Button data-testid="admin-submit" type="submit" disabled={loading}
              className="w-full rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white h-12">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
        <div className="text-center text-sm text-[#8A8D8A] mt-6">
          Not the advisor? <Link to="/" className="text-[#1A3626] underline-offset-4 hover:underline">Back to client login</Link>
        </div>
        <PrivacyDisclaimer compact/>
      </div>
    </div>
  );
}
